import React, { useRef } from 'react';
import {useTranslation} from 'react-i18next';import {useDrag} from '@use-gesture/react';import {useSpring, animated} from '@react-spring/web';import {Trash2} from 'lucide-react';import {AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger} from '@/components/ui/alert-dialog';
export default function SwipeableBookmarkItem({ bookmark, onSelect, onDelete }) {
  const { t } = useTranslation();
  const [{ x }, api] = useSpring(() => ({ x: 0 }));
  const deleteTriggerRef = useRef(null);

  const bind = useDrag(({ active, movement: [mx], cancel }) => {
    if (mx > 0) {
      if (active) api.start({ x: 0, immediate: true });
      return;
    }
    const TRIGGER_THRESHOLD = -75;
    if (!active && mx < TRIGGER_THRESHOLD) {
      if (deleteTriggerRef.current) deleteTriggerRef.current.click();
      api.start({ x: 0 });
    } else {
      api.start({ x: active ? mx : 0, immediate: active });
    }
  }, {
    axis: 'x',
    filterTaps: true,
    from: () => [x.get(), 0],
  });

  return (
    <div className="relative mb-1.5 rounded-xl overflow-visible touch-pan-y group">
      {/* Red background revealed on swipe */}
      <div className="absolute inset-0 bg-destructive/10 flex items-center justify-end px-4 rounded-xl">
        <Trash2 className="h-5 w-5 text-destructive" />
      </div>

      <animated.div
        {...bind()}
        style={{ x }}
        className="relative rounded-xl border border-transparent hover:border-border bg-card hover:bg-muted/30 transition-colors cursor-pointer"
        data-testid={`bookmark-item-${bookmark.bookmark_id}`}
      >
        <div
          className="px-3 py-2.5 select-none"
          onClick={() => { if (bookmark.conversation_id) onSelect(bookmark.conversation_id, bookmark.message_id); }}
        >
          <p className="text-sm text-foreground line-clamp-3 leading-relaxed">
            {bookmark.content ? bookmark.content.slice(0, 150) + (bookmark.content.length > 150 ? '...' : '') : 'Bookmarked response'}
          </p>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[10px] text-muted-foreground/50">
              {bookmark.created_at ? new Date(bookmark.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
            </span>
          </div>
        </div>

        {/* Hidden AlertDialog trigger - activated by swipe */}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              ref={deleteTriggerRef}
              className="sr-only"
              aria-hidden="true"
              tabIndex={-1}
              data-testid={`swipe-delete-trigger-${bookmark.bookmark_id}`}
            />
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('sidebar.delete_bookmark_title', 'Remove bookmark?')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('sidebar.delete_bookmark_desc', 'Are you sure you want to remove this bookmark? This cannot be undone.')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('chat.cancel', 'Cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={() => onDelete(bookmark.bookmark_id)} data-testid={`confirm-delete-bookmark-${bookmark.bookmark_id}`}>
                {t('chat.delete', 'Delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </animated.div>
    </div>
  );
}
